export interface SegmentUris { init: string; segments: string[] }

/** Parse an HLS media (variant) playlist into its init + media segment URIs. */
export function parseSegmentUris(playlist: string): SegmentUris {
  let init = "";
  const segments: string[] = [];
  for (const raw of playlist.split(/\r?\n/)) {
    const line = raw.trim();
    if (line.length === 0) continue;
    const mapMatch = /^#EXT-X-MAP:URI="([^"]+)"/.exec(line);
    if (mapMatch !== null) { init = mapMatch[1] ?? ""; continue; }
    if (line.startsWith("#")) continue;
    segments.push(line);
  }
  return { init, segments };
}

function resolve(base: string, uri: string): string {
  return new URL(uri, base).toString();
}

export interface ReassembleOpts {
  authToken?: string;
  workerOrigin?: string;
  onProgress?: (done: number, total: number) => void;
  signal?: AbortSignal;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new DOMException("reassembly aborted", "AbortError");
}

/** Reassemble one CMAF rung into a single progressive-playable fMP4 blob by
 *  byte-concatenating its init + media segments in order.
 *
 *  The variant playlist is served by the Worker (/api/*) and needs the team
 *  JWT; the segment URIs it returns are presigned R2 URLs and must be fetched
 *  WITHOUT an Authorization header. We attach the Bearer token only to URLs on
 *  workerOrigin. */
export async function reassembleRung(
  playlistUrl: string,
  opts: ReassembleOpts = {},
): Promise<Blob> {
  const authed = (url: string): RequestInit | undefined => {
    if (opts.authToken === undefined || opts.workerOrigin === undefined) return undefined;
    let sameOrigin = false;
    try {
      sameOrigin = new URL(url).origin === new URL(opts.workerOrigin).origin;
    } catch {
      sameOrigin = false;
    }
    return sameOrigin
      ? { headers: { Authorization: `Bearer ${opts.authToken}` } }
      : undefined;
  };
  const init = (url: string): RequestInit => ({ ...(authed(url) ?? {}), signal: opts.signal });

  const res = await fetch(playlistUrl, init(playlistUrl));
  if (!res.ok) throw new Error(`rung playlist fetch failed: HTTP ${res.status}`);
  const { init: initUri, segments } = parseSegmentUris(await res.text());
  if (initUri.length === 0) {
    throw new Error("rung playlist has no #EXT-X-MAP init segment");
  }
  const uris = [initUri, ...segments].map((u) => resolve(playlistUrl, u));
  opts.onProgress?.(0, uris.length);
  const parts: Blob[] = [];
  let done = 0;
  for (const url of uris) {
    throwIfAborted(opts.signal);
    const segRes = await fetch(url, init(url));
    if (!segRes.ok) throw new Error(`segment fetch failed: ${url}`);
    parts.push(await segRes.blob());
    done += 1;
    opts.onProgress?.(done, uris.length);
  }
  return new Blob(parts, { type: "video/mp4" });
}
